/**
 * Organizes cities by state and creates column structure
 * @param {Array} cities - Array of city objects
 * @param {string} initialHeader - Initial header for the first column
 * @returns {Object} - Contains organizedColumns and stateGroupings
 */
export const organizeCitiesByState = (cities, initialHeader) => {
  const stateNames = {
    'AC': 'Acre', 'AL': 'Alagoas', 'AP': 'Amapá', 'AM': 'Amazonas',
    'BA': 'Bahia', 'CE': 'Ceará', 'DF': 'Distrito Federal', 'ES': 'Espírito Santo',
    'GO': 'Goiás', 'MA': 'Maranhão', 'MT': 'Mato Grosso', 'MS': 'Mato Grosso do Sul',
    'MG': 'Minas Gerais', 'PA': 'Pará', 'PB': 'Paraíba', 'PR': 'Paraná',
    'PE': 'Pernambuco', 'PI': 'Piauí', 'RJ': 'Rio de Janeiro', 'RN': 'Rio Grande do Norte',
    'RS': 'Rio Grande do Sul', 'RO': 'Rondônia', 'RR': 'Roraima', 'SC': 'Santa Catarina',
    'SP': 'São Paulo', 'SE': 'Sergipe', 'TO': 'Tocantins'
  };

  const citiesByState = cities.reduce((acc, city) => {
    if (!acc[city.state]) {
      acc[city.state] = [];
    }
    acc[city.state].push(city);
    return acc;
  }, {});

  const sortedStates = Object.keys(citiesByState).sort();
  const organizedColumns = [initialHeader];
  const stateGroupings = [''];

  sortedStates.forEach(state => {
    const sortedCities = citiesByState[state].sort((a, b) => a.city.localeCompare(b.city));
    const stateDisplayName = stateNames[state] || state;

    sortedCities.forEach((city, index) => {
      organizedColumns.push(`${city.city} - ${city.state}`);
      stateGroupings.push(index === 0 ? stateDisplayName : '');
    });
  });

  return { organizedColumns, stateGroupings };
};

/**
 * Creates initial rows structure based on specialties
 * @param {Array} specialties - Array of specialty objects
 * @param {number} columnCount - Number of columns
 * @returns {Array} - Array of rows
 */
export const createInitialRows = (specialties, columnCount) => {
  return specialties.map(specialty => [
    specialty.name, 
    ...Array(columnCount - 1).fill('')
  ]);
};

/**
 * Processes specialty data for hints and separation lines
 * @param {Array} specialties - Array of specialty objects
 * @returns {Object} - Contains hints, separationLines, and fixedFactors
 */
export const processSpecialtyData = (specialties) => {
  const hints = specialties.map(specialty => [specialty.hint]);
  const separationLines = specialties.map(specialty =>
    Array.isArray(specialty.separationLine) 
      ? specialty.separationLine 
      : [specialty.separationLine]
  );
  const fixedFactors = specialties.map(specialty => 
    specialty.networkAdjustmentFactorFixedToOneHundred
  );

  return { hints, separationLines, fixedFactors };
};

/**
 * Populates rows with existing data
 * @param {Array} rows - Current rows array
 * @param {Array} cityNames - Array of city names
 * @param {Array} data - Existing data from API
 */
export const populateRowsWithData = (rows, cityNames, data) => {
  if (!data || !Array.isArray(data)) {
    console.warn('populateRowsWithData: data is not an array or is undefined');
    return;
  }
  
  data.forEach(({ city, attribute: specialtyData, value, networkAdjustmentFactor }) => {
    if (!city || !specialtyData || !city.city || !specialtyData.name) {
      console.warn('populateRowsWithData: invalid data structure', { city, specialtyData });
      return;
    }
    
    const specialtyIndex = rows.findIndex(row => row[0] === specialtyData.name);
    const cityIndex = cityNames.findIndex(name => name === `${city.city} - ${city.state}`);
    
    if (specialtyIndex !== -1 && cityIndex !== -1) {
      rows[specialtyIndex][cityIndex] = [value, networkAdjustmentFactor];
    }
  });
};

/**
 * Transforms rows data to JSON format for API submission
 * @param {Array} updatedRows - Updated rows from table
 * @param {Array} cities - Cities array
 * @param {Array} completeCities - Complete cities data
 * @param {Array} completeSpecialties - Complete specialties data
 * @returns {Array} - JSON array for API
 */
export const transformRowsToJson = (updatedRows, cities, completeCities, completeSpecialties) => {
  const json = [];
  
  cities.forEach((city, index) => {
    if (index === 0) return;
    
    updatedRows.forEach((row) => {
      if (Array.isArray(row[index][0]) && row[index][0].length > 1) {
        row[index] = row[index][0];
      }
      
      let networkAdjustmentFactor = 0;
      if (row[index][0] !== '' && row[index][1] === '') {
        networkAdjustmentFactor = 100;
      } else if (row[index][0] === '' && row[index][1] !== '') {
        networkAdjustmentFactor = 0;
      } else if (row[index][1] !== '') {
        networkAdjustmentFactor = row[index][1];
      }

      json.push({
        "city": completeCities.find((c) => `${c.city} - ${c.state}` === city),
        "attribute": completeSpecialties.find(s => s.name === row[0][0]),
        "value": row[index] === '' ? 0 : row[index][0],
        "networkAdjustmentFactor": networkAdjustmentFactor
      });
    });
  });

  return json;
};
